'use client';

import AutoImageModal from '../components/modals/AutoImageModal'
import { useEffect, useState } from 'react'
import useSWR, { mutate } from "swr";
import apiService from '@/app/services/apiService'
import ReservationCard from './ReservationCard'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { motion, AnimatePresence } from 'framer-motion'
import { CalendarCheck, Clock } from 'lucide-react'
import { UserReservationsSectionProps } from '@/types/type'

export default function UserReservationsSection({
  reservations,
  user,
  refreshUserData,
}: UserReservationsSectionProps) {
  const [tab, setTab] = useState<'ativas' | 'concluidas'>('ativas')

  const endpoint =
    tab === 'ativas'
      ? '/api/auth/myreservations/'
      : '/api/auth/myreservations/history/'

  // use as reservas passadas por prop como "fallback" apenas na aba ativas
  const fallbackData = tab === 'ativas' ? reservations : undefined

  const { data, isLoading, error } = useSWR<any[]>(
    endpoint,
    apiService.getWithToken,
    {
      dedupingInterval: 0,
      revalidateOnFocus: false,
      fallbackData,         // <- importante
    }
  )

  // revalidar quando trocar de aba/endpoint
  useEffect(() => {
    mutate(endpoint)
  }, [tab, endpoint])

  const renderContent = () => {
    if (isLoading) return <p>Carregando reservas...</p>
    if (error) return <p className="text-red-500">Erro ao carregar reservas.</p>

    if (!data || data.length === 0) {
      return (
        <>
          <motion.p
            key="empty"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="text-center text-gray-600"
          >
            Nenhuma reserva {tab === 'ativas' ? 'ativa' : 'concluída'} encontrada.
          </motion.p>

          <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center transition-all duration-300">
            <AutoImageModal
              imageUrl="/images/sponsors/apoio-local5.png"
              altText="Bem-vindo às suas reservas"
              showOnce={true}
              storageKey="reservationModalShown"
            />

            <img
              src="/images/sponsors/apoio-local5.png"
              alt="Apoio"
              className="w-48 h-48 mb-6 animate-spin-y-slow"
            />

            <p className="text-gray-600 mb-6">
              {tab === 'ativas'
                ? 'Você ainda não fez reserva em nossa plataforma. Veja nossos artistas disponíveis!'
                : 'Seu histórico está vazio por enquanto.'}
            </p>

            <a
              href="/"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition"
            >
              Explorar artistas
            </a>
          </div>
        </>
      )
    }

    return (
      <motion.div
        key={tab}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.25 }}
        className="space-y-6"
      >
        {/* Usar data, não reservations */}
        {data.map((reservation: any) => (
          <ReservationCard
            key={reservation.id}
            reservation={reservation}
            user={user}
            refreshUserData={refreshUserData}
          />
        ))}
      </motion.div>
    )
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-4">
      <Tabs value={tab} onValueChange={(val) => setTab(val as 'ativas' | 'concluidas')} className="w-full">
        <TabsList className="w-full justify-center mb-6">
          <TabsTrigger value="ativas" className="flex items-center gap-2">
            <CalendarCheck size={16} />
            Minhas Reservas
          </TabsTrigger>

          <TabsTrigger value="concluidas" className="flex items-center gap-2">
            <Clock size={16} />
            Histórico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ativas">
          <AnimatePresence mode="wait">
            {tab === 'ativas' && renderContent()}
          </AnimatePresence>
        </TabsContent>

        <TabsContent value="concluidas">
          <AnimatePresence mode="wait">
            {tab === 'concluidas' && renderContent()}
          </AnimatePresence>
        </TabsContent>
      </Tabs>
    </div>
  )
}
